import { Fragment, createElement, useCallback, useMemo, useRef } from 'react';
import { useTeleport } from './useTeleport';

/**
 * Use a modal pop-out behavior with a React Element.
 *
 * By default, your Element will always render in a "home" position that you specify.
 * It can be popped out into a "remote" position by invoking the `popInto()` callback
 * of this hook; and returned to the "home" position with a `reset()` callback. Context
 * and component state are perfectly preserved and the component does not un-mount or
 * re-render during the process. This hook encapsulates the imperative DOM manipulation
 * necessary for this "teleportation" behavior. Refer to the {@link useTeleport} hook
 * for more information.
 *
 * To use the behavior, pass in any valid ReactElement as the first argument. The hook
 * will return another element and two callbacks for controlling the pop-out logic:
 *
 *      const myElement = <Component {...props} />
 *      const [portal, popOut, reset] = usePopOut(myElement);
 *
 * The `portal` value is a ReactElement that you can render in your component's output.
 * Where you render the `portal` defines the "home" location of your component. It should
 * be rendered unconditionally, even when your component is in pop-out mode.
 *
 * To switch to the popped-out mode, you must have a Ref to another DOM node as a target,
 * such as a Modal or Popover. Call `popOut(ref)` to teleport the component into the new
 * DOM node.
 *
 * To switch back to the original location, call `reset()`, and the component will
 * teleport back to the location where you rendered the `portal` element.
 *
 *
 * @see {@link useTeleport} for the teleportation implementation
 * @param innerComponent React Component that will teleport
 * @param teleportOptions Options for the useTeleport hook (optional)
 * @returns
 */
export function usePopOut(innerComponent, teleportOptions) {
  const [portal, relocate] = useTeleport(innerComponent, teleportOptions);
  const defaultTargetRef = useRef(null);
  const defaultTargetElement = useMemo(() => /*#__PURE__*/createElement(Fragment, null, /*#__PURE__*/createElement('div', {
    ref: defaultTargetRef
  }), portal), [portal]);
  const popInto = useCallback(target => relocate(target), [relocate]);
  const reset = useCallback(() => relocate(defaultTargetRef.current), [relocate]);
  return [defaultTargetElement, popInto, reset];
}